/** Recruitment Network · pure data + layout. No React here, so the graph maths is testable and
 *  hover/selection never recomputes it — layout depends only on the data, the viewport size and
 *  the page of candidates shown. */
import type { AuthenticityBand, CandidacyState, PipelineStatus, Requisition } from "@talenthop/shared";
import type { Scan } from "../ResumeScoreList.js";

// ---------------------------------------------------------------- data shapes (existing APIs)

/** A row of GET /requisitions (Requisition + the counts and rich-spec config the list carries). */
export type NetRequisition = Requisition & {
  jobCode?: string | null;
  config: Requisition["config"] & {
    department?: string;
    location?: string;
    skills?: string[];
    requiredSkills?: string[];
  };
};

/** The candidate slice of GET /requisitions/{id}/funnel that the network needs. */
export interface FunnelCandidateLite {
  candidacyId: string;
  name: string | null;
  email?: string | null;
  state: CandidacyState;
  resumeScore: number | null;
  /** Capability of the current competency evaluation (the pre-0105 name; same value as competencyScore). */
  score?: number | null;
  /** The question-level INTERVIEW score (0105) and the separate COMPETENCY score; never blended. */
  interviewScore?: number | null;
  competencyScore?: number | null;
  /** 0100: the shared ranking tier (coverage- and criteria-version-aware); 4 = not evaluated. */
  rankTier?: number;
  /** 1 = best: the API's ranking (shared rankBy). The only source of interview ordering. */
  rank?: number;
  band?: AuthenticityBand | null;
  /** Recruiter's own tag (migration 0077) — separate from the system `state`. */
  pipelineStatus?: PipelineStatus | null;
  /** When the candidate was added / invited / last finished an interview (ISO). */
  createdAt?: string | null;
  invitedAt?: string | null;
  interviewedAt?: string | null;
}

export type StageKey = "received" | "scanning" | "invited" | "completed";

export const STAGES: { key: StageKey; label: string; hint: string }[] = [
  { key: "received", label: "Applications Received", hint: "Every candidate on this requirement — emailed in, uploaded or imported" },
  { key: "scanning", label: "Resume Scanning", hint: "Resumes the AI has scored against the requirement" },
  { key: "invited", label: "Interview Invitation", hint: "Candidates a recruiter invited to the AI interview" },
  { key: "completed", label: "Interview Completed", hint: "Interviews finished and scored" },
];

/** Candidacy states that mean "an invite went out" (cumulative: finishing still counts). */
const INVITED_STATES = new Set<CandidacyState>([
  "invited", "self_scheduled", "reminded", "interviewing",
  "scored", "advanced", "proposed_cut", "cut_notified", "finalist",
]);
const COMPLETED_STATES = new Set<CandidacyState>([
  "scored", "advanced", "proposed_cut", "cut_notified", "finalist",
]);

/**
 * One glanceable status per candidate, for the card badge and the toolbar filter.
 *  - rejected:    a PERSON said no — the recruiter's `rejected` tag, or a cut already notified.
 *                 An engine-proposed cut (`proposed_cut`) is NOT shown as rejected: rejection is
 *                 human-gated, so until someone decides it reads as "interviewed".
 *  - progressing: shortlisted / hired tag, or advanced / finalist / shortlisted state.
 *  - interviewed: the interview is finished and scored, no decision yet.
 *  - pending:     everything else — awaiting scan, review, invite or interview.
 */
export type CandidateStatus = "progressing" | "rejected" | "interviewed" | "pending";

export const STATUS_META: Record<CandidateStatus, { label: string; hint: string }> = {
  progressing: { label: "Progressing", hint: "Shortlisted, advanced or hired by your team" },
  rejected: { label: "Rejected", hint: "Rejected or notified by your team" },
  interviewed: { label: "Interviewed", hint: "Interview completed, awaiting a decision" },
  pending: { label: "Pending", hint: "Awaiting scan, review, invite or interview" },
};

export type StatusFilter = "all" | CandidateStatus;

export function statusOf(state: CandidacyState | null, tag: PipelineStatus | null | undefined): CandidateStatus {
  if (tag === "rejected" || state === "cut_notified") return "rejected";
  if (tag === "shortlisted" || tag === "hired" || state === "advanced" || state === "finalist" || state === "shortlisted")
    return "progressing";
  if (state != null && COMPLETED_STATES.has(state)) return "interviewed";
  return "pending";
}

export interface NetCandidate {
  id: string;
  name: string;
  email: string | null;
  /** Resume fit against the requirement (the AI match), 0–100. */
  match: number | null;
  interviewScore: number | null;
  /** The separate competency score (capability on the requirement's criteria). */
  competencyScore?: number | null;
  /** Shared ranking tier (0 best … 4 not evaluated) — a thin or outdated evaluation never outranks a well-covered current one. */
  rankTier: number;
  /** Position in the API's ranking of the requirement (1 = best). */
  rank: number;
  band: AuthenticityBand | null;
  years: number | null;
  /** Requirement must-haves the resume evidences (must-haves minus the rubric's `missing`). */
  skills: string[];
  missing: string[];
  state: CandidacyState | null;
  status: CandidateStatus;
  /** The recruiter's raw tag, for the panel's status control. */
  pipelineStatus: PipelineStatus | null;
  candidacyId: string | null;
  scan: Scan | null;
  /** Epoch ms, for the toolbar sort. scannedAt = when the resume was scanned. */
  addedAt: number | null;
  scannedAt: number | null;
  invitedAt: number | null;
  interviewedAt: number | null;
}

const ms = (iso: string | null | undefined): number | null => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? null : t;
};

/** One candidate per person: every scan, plus candidacies added without a resume scan. */
export function mergeCandidates(
  req: NetRequisition | undefined,
  scans: Scan[],
  funnel: FunnelCandidateLite[],
): NetCandidate[] {
  const byCandidacy = new Map(funnel.map((f) => [f.candidacyId, f]));
  const mustHaves = req?.config.requiredSkills?.length ? req.config.requiredSkills : (req?.config.skills ?? []);
  const out: NetCandidate[] = [];
  const seen = new Set<string>();

  for (const s of scans) {
    const f = s.candidacyId ? byCandidacy.get(s.candidacyId) : undefined;
    if (s.candidacyId) seen.add(s.candidacyId);
    const missing = s.rubric?.missing ?? [];
    const missingLower = new Set(missing.map((m) => m.toLowerCase()));
    out.push({
      id: s.id,
      name: s.candidateName || f?.name || s.filename,
      email: s.candidateEmail ?? f?.email ?? null,
      match: s.overall ?? f?.resumeScore ?? null,
      interviewScore: f?.interviewScore ?? null,
      competencyScore: f?.competencyScore ?? f?.score ?? null,
      rankTier: f?.rankTier ?? 4,
      rank: f?.rank ?? Number.MAX_SAFE_INTEGER,
      band: f?.band ?? null,
      years: s.rubric?.facts.evidencedYears ?? null,
      // Only claim a skill when the rubric actually judged must-haves — no rubric, no chips.
      skills: s.rubric ? mustHaves.filter((m) => !missingLower.has(m.toLowerCase())) : [],
      missing,
      state: f?.state ?? null,
      status: statusOf(f?.state ?? null, f?.pipelineStatus),
      pipelineStatus: f?.pipelineStatus ?? null,
      candidacyId: s.candidacyId,
      scan: s,
      addedAt: ms(f?.createdAt) ?? ms(s.createdAt),
      scannedAt: ms(s.createdAt),
      invitedAt: ms(f?.invitedAt ?? s.invitedAt),
      interviewedAt: ms(f?.interviewedAt),
    });
  }
  for (const f of funnel) {
    if (seen.has(f.candidacyId)) continue;
    out.push({
      id: f.candidacyId,
      name: f.name || f.email || "Unnamed candidate",
      email: f.email ?? null,
      match: f.resumeScore,
      interviewScore: f.interviewScore ?? null,
      competencyScore: f.competencyScore ?? f.score ?? null,
      rankTier: f.rankTier ?? 4,
      rank: f.rank ?? Number.MAX_SAFE_INTEGER,
      band: f.band ?? null,
      years: null,
      skills: [],
      missing: [],
      state: f.state,
      status: statusOf(f.state, f.pipelineStatus),
      pipelineStatus: f.pipelineStatus ?? null,
      candidacyId: f.candidacyId,
      scan: null,
      addedAt: ms(f.createdAt),
      scannedAt: null,
      invitedAt: ms(f.invitedAt),
      interviewedAt: ms(f.interviewedAt),
    });
  }
  return out;
}

/** Toolbar search: every word must appear in the candidate's name, email, evidenced skills or CV
 *  filename (case-insensitive), so "java priya" finds Priya with Java. Blank matches everyone. */
export function matchesSearch(c: NetCandidate, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [c.name, c.email ?? "", c.scan?.filename ?? "", ...c.skills].join(" ").toLowerCase();
  return words.every((w) => hay.includes(w));
}

export function inStage(c: NetCandidate, stage: StageKey): boolean {
  switch (stage) {
    case "received":
      return true;
    case "scanning":
      return c.match != null;
    case "invited":
      return c.state != null && INVITED_STATES.has(c.state);
    case "completed":
      return c.state != null && COMPLETED_STATES.has(c.state);
  }
}

/** Best fit first, the same order the resumes API returns (overall desc, unscored last). After
 *  the interview the interview score leads — it is the more complete signal — resume fit breaks
 *  ties. Ranking only orders the list; a person decides. */
export function rankForStage(list: NetCandidate[], stage: StageKey): NetCandidate[] {
  // After the interview: the API's ranking (shared rankBy: tier, capability, coverage, then resume fit,
  // name, id). No ordering rule of its own lives here (final audit P1-9).
  if (stage === "completed") {
    return [...list].sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  }
  return [...list].sort((a, b) => (b.match ?? -1) - (a.match ?? -1) || a.name.localeCompare(b.name));
}

/** Toolbar sort. "best" is the stage's own ranking (fit, then interview rank); the rest reorder the
 *  cards only — the rank badge on each card stays its position in the "best" order. */
export type SortKey =
  | "best" | "resumeDesc" | "resumeAsc" | "interviewDesc" | "interviewAsc"
  | "addedDesc" | "addedAsc" | "scannedDesc" | "invitedDesc" | "interviewedDesc" | "nameAsc";

export const SORTS: { key: SortKey; label: string }[] = [
  { key: "best", label: "Best match (default)" },
  { key: "resumeDesc", label: "Resume score · high to low" },
  { key: "resumeAsc", label: "Resume score · low to high" },
  { key: "interviewDesc", label: "Interview score · high to low" },
  { key: "interviewAsc", label: "Interview score · low to high" },
  { key: "addedDesc", label: "Last added" },
  { key: "addedAsc", label: "First added" },
  { key: "scannedDesc", label: "Last resume scanned" },
  { key: "invitedDesc", label: "Last invited" },
  { key: "interviewedDesc", label: "Last interviewed" },
  { key: "nameAsc", label: "Name · A to Z" },
];

/** Stable sort; candidates missing the sorted value go last in either direction. `list` arrives in "best" order. */
export function sortCandidates(list: NetCandidate[], key: SortKey): NetCandidate[] {
  if (key === "best") return list;
  const pick: Record<Exclude<SortKey, "best">, [(c: NetCandidate) => number | string | null, 1 | -1]> = {
    resumeDesc: [(c) => c.match, -1], resumeAsc: [(c) => c.match, 1],
    interviewDesc: [(c) => c.interviewScore, -1], interviewAsc: [(c) => c.interviewScore, 1],
    addedDesc: [(c) => c.addedAt, -1], addedAsc: [(c) => c.addedAt, 1],
    scannedDesc: [(c) => c.scannedAt, -1], invitedDesc: [(c) => c.invitedAt, -1],
    interviewedDesc: [(c) => c.interviewedAt, -1], nameAsc: [(c) => c.name.toLowerCase(), 1],
  };
  const [get, dir] = pick[key];
  return [...list].sort((a, b) => {
    const x = get(a), y = get(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return x === y ? 0 : (x < y ? -1 : 1) * dir;
  });
}

export function stageCounts(all: NetCandidate[]): Record<StageKey, number> {
  const counts = { received: 0, scanning: 0, invited: 0, completed: 0 } as Record<StageKey, number>;
  for (const c of all) for (const s of STAGES) if (inStage(c, s.key)) counts[s.key]++;
  return counts;
}

// ---------------------------------------------------------------- layout

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface LayoutNode extends Box {
  id: string;
}

export const SIZE = {
  req: { w: 300, h: 204 },
  stage: { w: 262, h: 112 },
  // 116 tall: details + score on top, then a fixed tag band below with room between (Oct 2026).
  cand: { w: 252, h: 116 },
  hub: { w: 240, h: 92 },
  constellation: { w: 236, h: 100 },
};

/** How many candidates a page shows: two columns of as many rows as the viewport can hold at a
 *  readable zoom (≥ 0.85), never fewer than 3 rows or more than 8. Rendering everyone would bury
 *  the ranking and cost frames at a few hundred candidates. */
export function candidatePageSize(viewportH: number): number {
  const rows = Math.floor((viewportH / 0.85 + 18) / (SIZE.cand.h + 18));
  return 2 * Math.min(8, Math.max(3, rows));
}

export const stageNodeId = (s: StageKey): string => `stage:${s}`;
export const candNodeId = (id: string): string => `cand:${id}`;
export const REQ_NODE = "req";

export interface FocusedLayout {
  nodes: LayoutNode[];
  /** candidate ids per column, top to bottom (column-major ranking: left column = best). */
  columns: [string[], string[]];
}

/**
 * Focused network: requisition → four vertical stages → candidates in two balanced columns.
 * Columns are column-major (ranks 1..⌈n/2⌉ on the left), so the strongest fits sit nearest the
 * stage they came from. Horizontal gaps grow into spare viewport width (capped), vertical gaps
 * into spare height, so the graph fills the canvas instead of huddling in a corner.
 */
export function computeFocusedLayout(opts: {
  viewportW: number;
  viewportH: number;
  candidateIds: string[];
}): FocusedLayout {
  const { viewportW, viewportH, candidateIds } = opts;
  const n = candidateIds.length;
  const rows = Math.max(1, Math.ceil(n / 2));
  const colGap = 20;

  // vertical rhythm — grow toward the viewport, within bounds
  const stageGap = clamp((viewportH * 0.9 - 4 * SIZE.stage.h) / 3, 18, 44);
  const candGap = clamp((viewportH * 0.9 - rows * SIZE.cand.h) / Math.max(1, rows - 1), 16, 34);
  const stageColH = 4 * SIZE.stage.h + 3 * stageGap;
  const candColH = rows * SIZE.cand.h + (rows - 1) * candGap;
  const totalH = Math.max(SIZE.req.h, stageColH, candColH);

  // horizontal rhythm
  let gap1 = 64;
  let gap2 = 84;
  const natural = SIZE.req.w + gap1 + SIZE.stage.w + gap2 + 2 * SIZE.cand.w + colGap;
  const spare = viewportW * 0.94 - natural;
  if (spare > 0) {
    gap1 = Math.min(140, gap1 + spare * 0.35);
    gap2 = Math.min(200, gap2 + spare * 0.65);
  }

  const nodes: LayoutNode[] = [];
  const reqX = 0;
  nodes.push({ id: REQ_NODE, x: reqX, y: (totalH - SIZE.req.h) / 2, ...SIZE.req });

  const stageX = reqX + SIZE.req.w + gap1;
  const stageTop = (totalH - stageColH) / 2;
  STAGES.forEach((s, i) => {
    nodes.push({ id: stageNodeId(s.key), x: stageX, y: stageTop + i * (SIZE.stage.h + stageGap), ...SIZE.stage });
  });

  const candX = stageX + SIZE.stage.w + gap2;
  const candTop = (totalH - candColH) / 2;
  const columns: [string[], string[]] = [[], []];
  candidateIds.forEach((id, i) => {
    const col = i < rows ? 0 : 1;
    const row = col === 0 ? i : i - rows;
    columns[col].push(id);
    nodes.push({
      id: candNodeId(id),
      x: candX + col * (SIZE.cand.w + colGap),
      y: candTop + row * (SIZE.cand.h + candGap),
      ...SIZE.cand,
    });
  });
  return { nodes, columns };
}

export const HUB_NODE = "hub";
export const reqNodeId = (id: string): string => `req:${id}`;

/**
 * Constellation: the org as a hub, every requisition on concentric (slightly elliptical) rings,
 * busiest first on the inner ring. Ring capacity grows with circumference so nodes never overlap.
 */
export function computeConstellationLayout(reqIds: string[]): LayoutNode[] {
  const nodes: LayoutNode[] = [{ id: HUB_NODE, x: -SIZE.hub.w / 2, y: -SIZE.hub.h / 2, ...SIZE.hub }];
  const { w, h } = SIZE.constellation;
  let placed = 0;
  let ring = 0;
  while (placed < reqIds.length) {
    const ry = 230 + ring * 170;
    const rx = ry * 1.55;
    // approximate ellipse circumference / node footprint → how many fit on this ring
    const circ = Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
    const capacity = Math.max(4, Math.floor(circ / (w + 40)));
    const count = Math.min(capacity, reqIds.length - placed);
    const offset = ring % 2 ? Math.PI / count : 0;
    for (let j = 0; j < count; j++) {
      const a = -Math.PI / 2 + offset + (j * 2 * Math.PI) / count;
      nodes.push({
        id: reqNodeId(reqIds[placed + j]!),
        x: Math.cos(a) * rx - w / 2,
        y: Math.sin(a) * ry - h / 2,
        w,
        h,
      });
    }
    placed += count;
    ring++;
  }
  return nodes;
}

export function boundsOf(nodes: Box[]): Box {
  if (!nodes.length) return { x: 0, y: 0, w: 1, h: 1 };
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const n of nodes) {
    x0 = Math.min(x0, n.x);
    y0 = Math.min(y0, n.y);
    x1 = Math.max(x1, n.x + n.w);
    y1 = Math.max(y1, n.y + n.h);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Horizontal S-curve from the right edge of `a` to the left edge of `b`. */
export function edgePath(a: Box, b: Box): string {
  const x1 = a.x + a.w;
  const y1 = a.y + a.h / 2;
  const x2 = b.x;
  const y2 = b.y + b.h / 2;
  const dx = Math.max(30, (x2 - x1) * 0.5);
  return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
}

/** Straight-ish link between two box centres (constellation spokes). */
export function spokePath(a: Box, b: Box): string {
  const ax = a.x + a.w / 2, ay = a.y + a.h / 2, bx = b.x + b.w / 2, by = b.y + b.h / 2;
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  return `M${ax},${ay} Q${mx + (by - ay) * 0.08},${my - (bx - ax) * 0.08} ${bx},${by}`;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
